<template>
    <div class="space-y-4">
        <div
            v-for="group in groupedPayouts"
            :key="group.key"
            class="bg-white rounded-2xl border border-gray-100 overflow-hidden"
        >
            <!-- Group Header -->
            <div
                @click="toggleDate(group.key)"
                class="flex justify-between items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
            >
                <div class="flex items-center gap-3">
                    <span class="w-2 h-2 rounded-full" :class="group.key === 'awaiting' ? 'bg-gold' : 'bg-sage'"></span>
                    <span class="text-sm font-medium text-gray-900">{{ formatGroupHeader(group.key) }}</span>
                    <span class="text-xs text-gray-400">{{ group.payouts.length }} item{{ group.payouts.length > 1 ? 's' : '' }}</span>
                </div>
                <div class="flex items-center gap-3">
                    <span class="text-sm font-semibold tabular-nums" :class="group.key === 'awaiting' ? 'text-gold-dark' : 'text-sage-dark'">${{ formatAmount(group.total) }}</span>
                    <svg
                        class="w-4 h-4 text-gray-300 transition-transform"
                        :class="{ 'rotate-180': expandedDates[group.key] }"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            <!-- Payout Items Table -->
            <transition name="expand">
                <div v-show="expandedDates[group.key]" class="border-t border-gray-100">
                    <!-- Table Header -->
                    <div class="hidden md:grid md:grid-cols-12 gap-1 px-4 py-1.5 bg-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        <div class="col-span-3">Name</div>
                        <div class="col-span-2">Agency</div>
                        <div class="col-span-2">Task</div>
                        <div class="col-span-1">Type</div>
                        <div class="col-span-2">Completed</div>
                        <div class="col-span-2 text-right">Amount</div>
                    </div>

                    <!-- Table Body -->
                    <div>
                        <div
                            v-for="(payout, index) in group.payouts"
                            :key="payout.id"
                            class="px-4 py-2"
                            :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'"
                        >
                            <!-- Desktop Row -->
                            <div class="hidden md:grid md:grid-cols-12 gap-1 items-center text-sm">
                                <div class="col-span-3 truncate text-gray-900">{{ payout.name }}</div>
                                <div class="col-span-2 truncate text-gray-600">{{ payout.agency || '-' }}</div>
                                <div class="col-span-2 text-gray-600">{{ payout.task }}</div>
                                <div class="col-span-1 text-gray-600">{{ payout.document_type || '-' }}</div>
                                <div class="col-span-2 text-gray-600 tabular-nums">{{ payout.coded_at || '-' }}</div>
                                <div class="col-span-2 text-right font-medium tabular-nums" :class="group.key === 'awaiting' ? 'text-gold-dark' : 'text-sage-dark'">${{ formatAmount(payout.amount) }}</div>
                            </div>

                            <!-- Mobile Card -->
                            <div class="md:hidden space-y-1">
                                <div class="flex justify-between items-start">
                                    <div>
                                        <div class="text-sm text-gray-900">{{ payout.name }}</div>
                                        <div class="text-xs text-gray-500">{{ payout.agency || '-' }}</div>
                                    </div>
                                    <span class="text-sm font-medium tabular-nums" :class="group.key === 'awaiting' ? 'text-gold-dark' : 'text-sage-dark'">${{ formatAmount(payout.amount) }}</span>
                                </div>
                                <div class="flex flex-wrap gap-x-3 text-xs text-gray-500">
                                    <span>{{ payout.task }}</span>
                                    <span>{{ payout.document_type }}</span>
                                    <span v-if="payout.coded_at">{{ payout.coded_at }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PendingPayouts',

    props: {
        payouts: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            expandedDates: {}
        };
    },

    computed: {
        groupedPayouts() {
            const groups = {};

            this.payouts.forEach(payout => {
                const key = payout.payable_at_key || 'awaiting';
                if (!groups[key]) {
                    groups[key] = {
                        key: key,
                        payouts: [],
                        total: 0
                    };
                }
                groups[key].payouts.push(payout);
                groups[key].total += parseFloat(payout.amount);
            });

            return Object.values(groups).sort((a, b) => {
                if (a.key === 'awaiting') return -1;
                if (b.key === 'awaiting') return 1;
                return b.key.localeCompare(a.key);
            });
        }
    },

    methods: {
        toggleDate(key) {
            this.$set(this.expandedDates, key, !this.expandedDates[key]);
        },

        formatGroupHeader(key) {
            if (key === 'awaiting') {
                return 'Awaiting Agency Payment';
            }
            const date = new Date(key);
            return 'Available ' + this.formatDateShort(date);
        },

        formatDateShort(date) {
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            const y = date.getFullYear();
            return `${m}/${d}/${y}`;
        },

        formatAmount(amount) {
            return parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
    }
};
</script>

<style scoped>
.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}

.expand-enter,
.expand-leave-to {
    opacity: 0;
    max-height: 0;
}

.expand-enter-to,
.expand-leave {
    opacity: 1;
    max-height: 2000px;
}
</style>
